import React from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  Flag,
  Landmark,
  Lightbulb,
  Sparkles,
  XCircle,
} from "lucide-react";
import {
  ArticleCard,
  FAQItem,
  MediaCarousel,
  PodcastCard,
  SectionHead,
  VideoCard,
  foregroundOnSolidAccent,
  importIcon,
} from "../shared/categoryDetailShared";

/** ─ Overview tab: Ghana land purchase educational journey ─ */
export const LandPurchaseOverviewTab = ({ data, color }) => (
  <div className="space-y-10">
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-[2rem] border border-black/[0.06] shadow-[0_22px_60px_-28px_rgba(0,0,0,0.14)]"
    >
      <div className="pointer-events-none absolute inset-0 bg-[#faf9f5]" aria-hidden />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white via-white to-neutral-50/70"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-[20%] top-1/4 h-[18rem] w-[18rem] rounded-full blur-3xl"
        style={{ background: `${color}`, opacity: 0.06 }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-24 top-1/2 h-[26rem] w-[26rem] -translate-y-1/2 rounded-full opacity-[0.12] blur-3xl"
        style={{ background: `radial-gradient(circle, ${color}, transparent 70%)` }}
        aria-hidden
      />
      <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-black/[0.07] to-transparent" aria-hidden />

      <div className="relative grid lg:grid-cols-12 lg:gap-0">
        <div className="flex flex-col justify-center px-6 py-10 sm:p-10 lg:col-span-7 lg:px-11 lg:py-14 lg:pr-10">
          <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-3">
              <span className="h-[3px] w-11 shrink-0 rounded-full" style={{ background: color }} />
              <span className="text-[10px] font-black uppercase tracking-[0.32em]" style={{ color }}>
                Introduction
              </span>
            </div>
            <span className="hidden h-1 w-1 rounded-full bg-black/18 sm:inline" aria-hidden />
            <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
              <Landmark size={13} strokeWidth={2} style={{ color }} />
              Ghana land law essentials
            </span>
          </div>

          <h2 className="max-w-xl text-[1.65rem] font-black leading-[1.15] tracking-tight text-gray-950 sm:text-3xl sm:leading-[1.12]">
            {data.introduction.headline}
          </h2>
          <p className="mt-5 max-w-prose text-[0.9375rem] leading-[1.75] text-gray-600 sm:text-base sm:leading-8">
            {data.introduction.body}
          </p>

          <div className="mt-9 grid gap-3 sm:grid-cols-2">
            {data.introduction.keyFacts.map((fact) => (
              <div
                key={fact}
                className="group flex gap-3.5 rounded-2xl border border-black/[0.05] bg-white/90 p-4 shadow-[0_6px_24px_-16px_rgba(0,0,0,0.18)] backdrop-blur-sm ring-1 ring-white transition hover:border-black/[0.08] hover:shadow-[0_10px_32px_-18px_rgba(0,0,0,0.2)]"
              >
                <span
                  className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl shadow-sm transition group-hover:scale-[1.03]"
                  style={{ background: `${color}16`, color }}
                >
                  <CheckCircle2 size={17} strokeWidth={2.25} />
                </span>
                <p className="text-[13px] font-semibold leading-snug text-gray-800">{fact}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 overflow-hidden rounded-2xl border border-black/[0.06] bg-white shadow-[0_14px_44px_-32px_rgba(0,0,0,0.28)] ring-1 ring-black/[0.03]">
            <div className="flex gap-5 px-6 py-5 sm:gap-6 sm:p-7">
              <span
                className="hidden w-1 shrink-0 self-stretch rounded-full sm:block"
                style={{
                  background: `linear-gradient(180deg, ${color}, ${color}55, transparent)`,
                }}
                aria-hidden
              />
              <div className="flex min-w-0 flex-1 gap-4">
                <span
                  className="mt-1 w-[3px] shrink-0 self-stretch rounded-full sm:hidden"
                  style={{ background: color }}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <div
                    className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.22em]"
                    style={{ color }}
                  >
                    <Sparkles size={12} strokeWidth={2} className="shrink-0 opacity-95" aria-hidden />
                    Bottom line
                  </div>
                  <p className="mt-2.5 text-sm font-bold leading-relaxed text-gray-900 sm:text-[0.9375rem] sm:leading-[1.65]">
                    {data.introduction.callout}
                  </p>
                </div>
                <ArrowRight
                  size={20}
                  strokeWidth={2.25}
                  className="hidden shrink-0 translate-x-0 self-start text-black/25 sm:block"
                  style={{ color }}
                  aria-hidden
                />
              </div>
            </div>
          </div>
        </div>

        <div className="relative flex min-h-[15rem] items-stretch lg:col-span-5 lg:min-h-0">
          <div className="relative flex w-full flex-1 px-6 pb-9 pt-1 sm:p-9 sm:pt-3 lg:px-10 lg:py-12 lg:pl-3 lg:pb-14">
            <div className="group relative min-h-[17rem] w-full overflow-hidden rounded-[1.375rem] ring-2 ring-black/[0.04] ring-offset-4 ring-offset-[#faf9f5] lg:min-h-[min(100%,24rem)]">
              <img
                src={data.galleryImages[0]}
                alt=""
                className="absolute inset-0 h-full w-full object-cover transition duration-[1.1s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.035]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-transparent to-black/20" />

              <div className="absolute inset-x-4 top-4 sm:inset-x-5 sm:top-5">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/40 px-3 py-1.5 backdrop-blur-md">
                  <span className="relative flex h-2 w-2">
                    <span
                      className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-40"
                      style={{ background: color }}
                    />
                    <span
                      className="relative inline-flex h-2 w-2 rounded-full"
                      style={{ background: color, boxShadow: `0 0 14px ${color}99` }}
                    />
                  </span>
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white">Consumer guide</span>
                </div>
              </div>

              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                <div className="rounded-[1.15rem] border border-white/20 bg-black/45 p-[1px] backdrop-blur-md">
                  <div className="rounded-[1.1rem] bg-gradient-to-b from-white/[0.12] to-transparent px-5 py-4 sm:px-6 sm:py-5">
                    <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
                      Land · Property · Ghana
                    </p>
                    <p className="mt-1.5 font-black leading-snug tracking-tight text-white [text-wrap:balance] sm:text-xl">
                      Know before you pay.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {["Verification", "Title", "Lease"].map((tag) => (
                        <span
                          key={tag}
                          className="rounded-lg border border-white/15 bg-white/[0.12] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white/95"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>

    <section>
      <SectionHead
        eyebrow="Understanding Ownership"
        title="Types of Land Ownership in Ghana"
        subtitle="Before buying land, you must understand how land ownership works in Ghana. Each type has different legal implications."
        color={color}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        {data.ownershipTypes.map((type, idx) => {
          const Icon = importIcon(type.icon);
          return (
            <motion.div
              key={type.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.08, duration: 0.4 }}
              className="relative overflow-hidden rounded-[1.75rem] border border-black/[0.07] bg-white p-6 shadow-sm"
            >
              <div
                className="absolute -right-8 -top-8 h-28 w-28 rounded-full opacity-[0.07] blur-2xl"
                style={{ background: color }}
                aria-hidden
              />
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl" style={{ background: `${color}18`, color }}>
                <Icon size={20} />
              </div>
              <span
                className="mb-2 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-[0.15em]"
                style={{ background: `${color}18`, color }}
              >
                {type.tag}
              </span>
              <h3 className="text-base font-black text-gray-950">{type.title}</h3>
              <p className="text-xs font-semibold text-gray-400">{type.subtitle}</p>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">{type.desc}</p>
            </motion.div>
          );
        })}
      </div>
      <div className="mt-4 rounded-2xl border border-amber-200/60 bg-amber-50 p-4" style={{ borderColor: `${color}50`, background: `${color}08` }}>
        <p className="text-xs font-bold text-gray-700">
          <span style={{ color }} className="font-black">
            Key Insight:{" "}
          </span>
          Land ownership in Ghana involves both <strong>customary law</strong> (traditions) and <strong>statutory law</strong> (the formal legal system) —
          this combination is what makes land transactions complex.
        </p>
      </div>
    </section>

    <section>
      <SectionHead
        eyebrow="Most Important Step"
        title="Land Purchase Verification"
        subtitle="This is where most people make critical mistakes. Before you pay ANY money, complete every step below."
        color={color}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {data.verificationSteps.map((step, idx) => {
          const StepIcon = importIcon(step.icon);
          return (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.07, duration: 0.38 }}
              className="relative overflow-hidden rounded-[1.75rem] border border-black/[0.07] bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div
                className="pointer-events-none absolute -bottom-10 -right-10 h-28 w-28 rounded-full opacity-[0.08] blur-3xl"
                style={{ background: color }}
                aria-hidden
              />
              <div className="mb-4 flex items-center gap-3">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-full border-2 bg-white text-sm font-black shadow-[0_0_22px_-6px_rgba(255,255,255,0.9),0_8px_24px_-8px_rgba(0,0,0,0.14)]"
                  style={{ borderColor: color, color }}
                >
                  {step.number}
                </div>
                <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${color}15`, color }}>
                  <StepIcon size={17} strokeWidth={2.1} />
                </div>
              </div>
              <h3 className="flex items-center gap-2 text-base font-black text-gray-950">
                <CheckCircle2 size={18} strokeWidth={2.25} className="shrink-0" style={{ color }} aria-hidden />
                {step.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">{step.desc}</p>
              <div
                className="mt-4 inline-flex max-w-full items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-bold"
                style={{ background: `${color}12`, color }}
              >
                <Lightbulb size={13} strokeWidth={2.25} className="shrink-0 opacity-90" aria-hidden />
                <span>{step.tip}</span>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-4 overflow-hidden rounded-[1.75rem] border border-red-100 bg-red-50">
        <div className="flex items-center gap-2.5 border-b border-red-100 px-6 py-4">
          <AlertTriangle size={17} className="shrink-0 text-red-500" aria-hidden />
          <span className="text-sm font-black text-red-700">Common mistakes to avoid</span>
        </div>
        <div className="grid gap-2 p-6 sm:grid-cols-2">
          {data.commonMistakes.map((m) => (
            <div key={m} className="flex items-start gap-2 text-sm text-red-700">
              <XCircle size={15} className="mt-0.5 shrink-0 text-red-400" />
              {m}
            </div>
          ))}
        </div>
      </div>
    </section>

    <section>
      <SectionHead
        eyebrow="Securing Your Land"
        title="Land Title Registration Process"
        subtitle="Buying land is NOT enough. You must register it to prove legal ownership, prevent re-sale, and protect yourself in court."
        color={color}
      />
      <div className="overflow-hidden rounded-[2rem] border border-black/[0.07] bg-white p-6 shadow-sm sm:p-8">
        <div className="relative space-y-0">
          {data.registrationSteps.map((r, idx) => (
            <div key={r.title} className="flex gap-5">
              <div className="flex flex-col items-center">
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-black shadow-sm"
                  style={{ background: color, color: foregroundOnSolidAccent(color) }}
                >
                  {r.step}
                </div>
                {idx < data.registrationSteps.length - 1 ? (
                  <div className="mt-1 w-[2px] flex-1 rounded-full opacity-25" style={{ background: color, minHeight: "2rem" }} />
                ) : null}
              </div>
              <div className={`pb-8 ${idx === data.registrationSteps.length - 1 ? "pb-0" : ""}`}>
                <p className="text-sm font-black text-gray-950">{r.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-gray-500">{r.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-7 rounded-2xl border p-5" style={{ borderColor: `${color}40`, background: `${color}06` }}>
          <p className="mb-4 text-[10px] font-black uppercase tracking-[0.25em]" style={{ color }}>
            Ghana has two registration systems
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {data.registrationSystems.map((sys) => (
              <div
                key={sys.name}
                className={`rounded-xl border p-4 ${
                  sys.recommended ? "" : "border-gray-200 bg-gray-50"
                }`}
                style={
                  sys.recommended
                    ? {
                        borderColor: `${color}44`,
                        background: `${color}0d`,
                      }
                    : undefined
                }
              >
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <p className="text-sm font-black text-gray-900">{sys.name}</p>
                  {sys.recommended ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wider"
                      style={{ background: `${color}22`, color }}
                    >
                      <CheckCircle2 size={10} strokeWidth={2.5} aria-hidden />
                      Recommended
                    </span>
                  ) : null}
                </div>
                <p className="text-xs leading-relaxed text-gray-600">{sys.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="overflow-hidden rounded-[2rem] border border-black/[0.07] bg-white shadow-sm">
      <div className="grid lg:grid-cols-2">
        <div className="p-7">
          <span className="mb-3 block text-[10px] font-black uppercase tracking-[0.28em] text-red-500">Land Disputes</span>
          <h3 className="mb-4 text-xl font-black text-gray-950">Common Causes of Disputes</h3>
          <div className="space-y-2.5">
            {data.disputeCauses.map((c) => (
              <div key={c} className="flex items-start gap-2.5 text-sm text-gray-700">
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-red-400" />
                {c}
              </div>
            ))}
          </div>
        </div>
        <div className="p-7" style={{ background: `${color}08`, borderLeft: `1px solid ${color}25` }}>
          <span className="mb-3 block text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            What to Do
          </span>
          <h3 className="mb-4 text-xl font-black text-gray-950">If You Have a Dispute</h3>
          <div className="space-y-2.5">
            {data.disputeActions.map((a, idx) => (
              <div key={a} className="flex items-start gap-3 text-sm text-gray-700">
                <div
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-black"
                  style={{ background: color, color: foregroundOnSolidAccent(color) }}
                >
                  {idx + 1}
                </div>
                {a}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section>
      <SectionHead
        eyebrow="Important — Leases"
        title="Land Lease Agreements"
        subtitle="In Ghana, most land is not sold forever — it is leased. Understanding lease terms is critical before signing anything."
        color={color}
      />
      <div className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="overflow-hidden rounded-[1.75rem] border border-black/[0.07] bg-white shadow-sm">
          <div className="border-b border-black/[0.06] px-6 py-4">
            <p className="text-sm font-black text-gray-950">Typical Lease Durations</p>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {data.leaseTerms.map((t) => (
                <div key={t.who} className="flex items-center justify-between rounded-xl border border-black/[0.06] px-4 py-3">
                  <div>
                    <p className="text-sm font-bold text-gray-900">{t.who}</p>
                    <p className="text-xs text-gray-400">Renewable: {t.renewable}</p>
                  </div>
                  <span
                    className="rounded-xl px-3 py-1.5 text-sm font-black"
                    style={{ background: color, color: foregroundOnSolidAccent(color) }}
                  >
                    {t.duration}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-5">
              <p className="mb-3 text-xs font-black uppercase tracking-[0.2em] text-gray-400">Must be included in lease</p>
              <div className="space-y-2">
                {data.leaseRequirements.map((r) => (
                  <div key={r} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 size={13} style={{ color }} className="shrink-0" />
                    {r}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-hidden rounded-[1.75rem] border border-orange-100 bg-orange-50">
          <div className="border-b border-orange-100 px-6 py-4">
            <p className="flex items-center gap-2 text-sm font-black text-orange-800">
              <AlertTriangle size={15} className="shrink-0 text-orange-500" aria-hidden />
              Things to watch out for
            </p>
          </div>
          <div className="space-y-3 p-6">
            {data.leaseWatchOut.map((w) => (
              <div key={w} className="flex items-start gap-3 rounded-xl bg-white/70 p-3 text-sm text-orange-800 shadow-sm">
                <XCircle size={14} className="mt-0.5 shrink-0 text-orange-400" />
                {w}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section>
      <SectionHead
        eyebrow="Key Institutions"
        title="Bodies You Must Know"
        subtitle="These official institutions play a critical role in any land transaction or dispute in Ghana."
        color={color}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {data.institutions.map((inst, idx) => {
          const InstIcon = importIcon(inst.icon);
          return (
            <motion.div
              key={inst.name}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.06 }}
              className="flex gap-4 rounded-[1.75rem] border border-black/[0.07] bg-white p-5 shadow-sm"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl" style={{ background: `${color}15`, color }}>
                <InstIcon size={19} />
              </div>
              <div className="min-w-0">
                <p className="font-black text-gray-950">{inst.name}</p>
                <p className="mt-1 text-sm leading-relaxed text-gray-500">{inst.desc}</p>
                {inst.linkHref ? (
                  <a
                    href={inst.linkHref}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs font-bold transition hover:underline"
                    style={{ color }}
                  >
                    {inst.linkLabel}
                    <ExternalLink size={11} />
                  </a>
                ) : (
                  <p className="mt-2 text-xs font-semibold text-gray-400">{inst.linkLabel}</p>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>

    <section className="overflow-hidden rounded-[2rem] border border-black/[0.07] bg-white shadow-sm">
      <div className="border-b border-black/[0.06] px-7 py-5">
        <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
          Budget For This
        </span>
        <h3 className="text-xl font-black text-gray-950">Costs Involved in Land Transactions</h3>
      </div>
      <div className="divide-y divide-black/[0.05]">
        {data.costItems.map((c, idx) => (
          <div key={c.item} className={`flex items-center justify-between px-7 py-4 ${idx % 2 === 0 ? "bg-white" : "bg-[#faf8f2]"}`}>
            <span className="text-sm font-bold text-gray-800">{c.item}</span>
            <span className="rounded-xl px-3 py-1 text-xs font-bold" style={{ background: `${color}18`, color }}>
              {c.typical}
            </span>
          </div>
        ))}
      </div>
    </section>

    <section className="grid gap-5 lg:grid-cols-2">
      <div className="overflow-hidden rounded-[2rem] border border-black/[0.08] shadow-sm" style={{ borderColor: `${color}40`, background: `${color}0a` }}>
        <div className="border-b border-black/[0.06] px-6 py-5" style={{ background: `${color}0d` }}>
          <span className="mb-1 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            <CheckCircle2 size={12} strokeWidth={2.5} aria-hidden />
            Practical tips
          </span>
          <h3 className="mt-1 flex items-center gap-2 text-xl font-black text-gray-950">
            <CheckCircle2 size={22} strokeWidth={2.25} className="shrink-0" style={{ color }} aria-hidden />
            What buyers must always do
          </h3>
        </div>
        <div className="space-y-2.5 p-6">
          {data.practicalTips.map((tip) => (
            <div
              key={tip}
              className="flex items-start gap-3 rounded-xl border border-black/[0.05] bg-white px-4 py-3 text-sm font-semibold text-gray-800 shadow-sm"
            >
              <CheckCircle2 size={16} strokeWidth={2.25} className="mt-0.5 shrink-0" style={{ color }} aria-hidden />
              {tip}
            </div>
          ))}
        </div>
      </div>
      <div className="overflow-hidden rounded-[2rem] border border-red-200 bg-red-50">
        <div className="border-b border-red-100 px-6 py-5">
          <span className="text-[10px] font-black uppercase tracking-[0.28em] text-red-500">Red Flags</span>
          <h3 className="mt-1 flex items-center gap-2 text-xl font-black text-gray-950">
            <Flag size={20} strokeWidth={2.25} className="shrink-0 text-red-500" aria-hidden />
            Warning signs to watch out for
          </h3>
        </div>
        <div className="space-y-2.5 p-6">
          {data.redFlags.map((flag) => (
            <div
              key={flag}
              className="flex items-start gap-3 rounded-xl bg-white/80 px-4 py-3 text-sm font-semibold text-red-800 shadow-sm"
            >
              <AlertTriangle size={15} className="mt-0.5 shrink-0 text-red-400" />
              {flag}
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="relative overflow-hidden rounded-[2rem] bg-gray-950 text-white">
      <div className="absolute inset-x-0 top-0 h-1 rounded-t-[2rem] opacity-90" style={{ background: color }} />
      <div className="relative p-8 sm:p-10">
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full opacity-[0.12] blur-3xl" style={{ background: color }} aria-hidden />
        <p className="mb-4 text-[10px] font-black uppercase tracking-[0.3em]" style={{ color }}>
          Final Takeaway
        </p>
        <h2 className="max-w-2xl text-2xl font-black leading-tight sm:text-3xl">
          In Ghana, land is not just about buying — it is about verifying, documenting, and protecting your ownership legally.
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-white/15 bg-white/[0.06] p-5">
            <p className="mb-1 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-white">
              <CheckCircle2 size={14} strokeWidth={2.25} style={{ color }} aria-hidden />
              <span style={{ color }}>Safe investment</span>
            </p>
            <p className="text-sm leading-relaxed text-white/70">Verify → Document → Register → Protect with a lawyer</p>
          </div>
          <div className="rounded-2xl border border-red-500/30 bg-red-950/40 p-5">
            <p className="mb-1 text-xs font-black uppercase tracking-wider text-red-400">Losing Your Money</p>
            <p className="text-sm leading-relaxed text-white/70">Skip verification → Trust verbal deals → No registration</p>
          </div>
        </div>
        <p className="mt-5 flex flex-wrap items-center gap-2 text-sm font-bold text-white/60">
          <ArrowRight size={16} strokeWidth={2.25} className="shrink-0 opacity-90" style={{ color }} aria-hidden />
          <span>
            The difference is <span className="text-white">legal due diligence.</span>
          </span>
        </p>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Frequently Asked" title="Common Questions" color={color} />
      <div className="space-y-2">
        {data.faqs.map((faq) => (
          <FAQItem key={faq.q} q={faq.q} a={faq.a} color={color} />
        ))}
      </div>
    </section>
  </div>
);

export const LandPurchaseArticlesTab = ({ data, color, industryId, slug, ctaTo }) => (
  <div className="space-y-10">
    <section>
      <div className="mb-7 flex items-end justify-between gap-4">
        <SectionHead
          eyebrow="Content Library"
          title="Articles &amp; Guides"
          subtitle="Expert-written content on land purchase, verification, and registration in Ghana."
          color={color}
        />
        <span className="mb-8 shrink-0 rounded-full px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {data.articles.length} articles
        </span>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.articles.map((article) => (
          <ArticleCard key={article.id} article={article} to={`/${industryId}/${slug}/${article.slug}`} color={color} />
        ))}
      </div>
    </section>

    <section
      className="overflow-hidden rounded-[2rem] p-8 sm:p-10"
      style={{ background: `linear-gradient(135deg, ${color}18, ${color}08)`, border: `1px solid ${color}35` }}
    >
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="mb-1 text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            Lead CTA
          </p>
          <h3 className="text-2xl font-black text-gray-950">Talk to a Property Lawyer</h3>
          <p className="mt-1 text-sm text-gray-500">Verify documents. Book a consultation. Get land-search assistance.</p>
        </div>
        <Link
          to={ctaTo}
          className="inline-flex shrink-0 items-center gap-2 rounded-2xl px-7 py-4 text-sm font-black shadow-md transition hover:scale-[1.02]"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          Register Free
          <ArrowRight size={15} />
        </Link>
      </div>
    </section>
  </div>
);

export const LandPurchaseMediaTab = ({ data, color }) => {
  const videos = data.videos ?? [];
  const podcasts = data.podcasts ?? [];
  const total = videos.length + podcasts.length;

  return (
    <div className="min-w-0 space-y-10 sm:space-y-12 md:space-y-14">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHead
          eyebrow="Watch &amp; listen"
          title="Videos &amp; podcasts"
          subtitle="Expert clips and episodes on land verification, registration, leases, and disputes — curated for Ghana buyers."
          color={color}
        />
        <span className="mt-1 shrink-0 rounded-full px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {total} featured
        </span>
      </div>

      {videos.length > 0 ? (
        <section aria-labelledby="land-media-videos-heading" className="min-w-0">
          <MediaCarousel
            variant="video"
            color={color}
            ariaLabel="Land purchase and verification videos"
            items={videos}
            header={<h3 id="land-media-videos-heading" className="text-lg font-black leading-tight text-gray-950 sm:text-xl">Videos</h3>}
            meta={`${videos.length} in library`}
            renderItem={(video) => <VideoCard video={video} color={color} />}
          />
        </section>
      ) : null}

      {podcasts.length > 0 ? (
        <section aria-labelledby="land-media-podcasts-heading" className="min-w-0">
          <MediaCarousel
            variant="podcast"
            color={color}
            ariaLabel="Land purchase and verification podcast episodes"
            items={podcasts}
            header={<h3 id="land-media-podcasts-heading" className="text-lg font-black leading-tight text-gray-950 sm:text-xl">Podcasts</h3>}
            meta={`${podcasts.length} episodes`}
            renderItem={(episode) => <PodcastCard podcast={episode} color={color} />}
          />
          <p className="mt-4 px-2 text-center text-xs leading-relaxed text-gray-400 sm:text-left sm:text-sm md:mt-6">
            Listen links appear here when Spotify, Apple Podcasts, or other feeds are connected from admin or your CMS.
          </p>
        </section>
      ) : null}

      <div className="overflow-hidden rounded-[1.5rem] bg-gray-950 text-white shadow-[0_20px_50px_-40px_rgba(0,0,0,0.45)] sm:rounded-[1.85rem] md:rounded-[2rem]">
        <div className="grid gap-0 lg:grid-cols-2">
          <div className="p-6 sm:p-8 md:p-10">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]">
              <Sparkles size={11} style={{ color }} />
              For partners
            </div>
            <h3 className="text-xl font-black leading-snug tracking-tight sm:text-2xl">Distribute verified video &amp; audio</h3>
            <p className="mt-3 text-sm leading-relaxed text-white/60">
              Law firms and land desks can syndicate episodic podcasts and replay-safe video explainers alongside Infoboxx — one trusted place buyers already use.
            </p>
            <Link
              to="/partners/onboard"
              className="mt-6 inline-flex items-center gap-2 rounded-2xl px-6 py-3.5 text-sm font-black"
              style={{ background: color, color: foregroundOnSolidAccent(color) }}
            >
              Become a partner
              <ArrowRight size={14} />
            </Link>
          </div>
          <div className="relative min-h-[13rem] sm:min-h-[15rem] lg:min-h-0">
            <img src={data.galleryImages[1]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-gray-950/70" />
          </div>
        </div>
      </div>
    </div>
  );
};
